import { EposPrinterConfig, useEposPrinter } from '@/hooks/use-epos-printer'
import { reportError } from '@/components/apps/error-boundary'
import React, { Context, createContext, useEffect, useRef } from 'react'

type PrinterDevice = {
  connection: any
  printer: any
}

type PrintImageParams = {
  canvas: HTMLCanvasElement
  filename: string
  hotelId: string
  connectIdKey: string
  isStayConnected?: boolean
  // レシート画像の記録アップロードの署名判定に使う（セルフCI/CO のみ）
  reservationUuid?: string
}

type ContextType = {
  isConnected: () => boolean
  connect: (config: EposPrinterConfig) => Promise<PrinterDevice>
  disconnect: () => void
  printImage: (params: PrintImageParams) => Promise<void>
}
const printerContext = createContext<ContextType | undefined>(undefined)

export const PrinterContextCreator = (): Context<ContextType> => {
  if (!printerContext) {
    throw new Error('undefined context')
  }
  return printerContext as Context<ContextType>
}

const disconnectedDevice: PrinterDevice = { connection: undefined, printer: undefined }

export const PrinterProvider = ({ children }) => {
  const eposPrinter = useEposPrinter()
  // 接続状態を描画に使う画面がないため state は持たず、非同期処理からも常に最新を参照できる ref で保持する
  const device = useRef<PrinterDevice>(disconnectedDevice)
  const connecting = useRef<Promise<PrinterDevice> | null>(null)

  const isConnected = (): boolean => Boolean(device.current.connection?.isConnected() && device.current.printer)

  const connect = async (config: EposPrinterConfig): Promise<PrinterDevice> => {
    if (isConnected()) {
      return device.current
    }
    // 接続確立まで数秒かかり、その間に接続要求が重なると二重接続になるため、実行中の接続処理の結果を共有する
    if (!connecting.current) {
      connecting.current = eposPrinter
        .initialize({
          ...config,
          // 自動再接続で通信が復帰しても、プリンター側ではデバイスが解放済みで印刷できないため、次の印刷で作り直す
          onDisconnect: () => {
            device.current = disconnectedDevice
          },
        })
        .then(connected => {
          device.current = connected
          return connected
        })
        .finally(() => {
          connecting.current = null
        })
    }
    return connecting.current
  }

  const disconnect = (): void => {
    try {
      eposPrinter.disconnect(device.current.connection, device.current.printer)
    } catch (e) {
      // 切断に失敗しても壊れた接続を掴み続けないよう、保持している接続は破棄する
      reportError(e)
    }
    device.current = disconnectedDevice
  }

  // 印刷を要求した時点で接続が切れている・printer が生成されていないことがあるため、接続を確保してから印刷する
  const printImage = async ({ canvas, filename, hotelId, connectIdKey, isStayConnected = false, reservationUuid }: PrintImageParams): Promise<void> => {
    const connected = await connect({ hotelId, connectIdKey })
    await eposPrinter.printImage(canvas, filename, hotelId, connected.printer, connected.connection, isStayConnected, reservationUuid)
  }

  // 接続を残したままだとプリンター側のセッションが解放されず、次回の接続に失敗するため切断する
  useEffect(() => {
    return () => eposPrinter.disconnect(device.current.connection, device.current.printer)
  }, [])

  return <printerContext.Provider value={{ isConnected, connect, disconnect, printImage }}>{children}</printerContext.Provider>
}
